// OpenAI Realtime (Direct Voice) client — WebRTC to gpt-4o-realtime.
//
// Flow:
//   1. POST /api/realtime/session → receive ephemeral session token
//   2. RTCPeerConnection with mic track + audio playback element
//   3. POST our SDP offer to /api/realtime/negotiate, receive answer
//   4. Full-duplex conversation with barge-in / interruption
//
// This module is opinionated to keep Voice Mode simple: one active
// session at a time, single audio element, cleanup on stop(). Everything
// gated at the backend (`voice_beta` + `direct_voice_beta`) — we still
// hide the entry point in the UI, but the endpoint is the source of
// truth.

import { API } from "@/lib/api";

const _EVENTS_CHANNEL = "oai-events";

// Feature-flag helper — mirrors the backend gate. Admins always,
// non-admin needs BOTH voice_beta and direct_voice_beta.
export function isDirectVoiceEnabledFor(user) {
  if (!user || typeof user !== "object") return false;
  if (user.role === "admin") return true;
  return !!(user.voice_beta && user.direct_voice_beta);
}

async function _postJSON(path) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = body?.detail || `HTTP ${res.status}`;
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = res.status;
    throw err;
  }
  return body;
}

// Fire-and-forget usage report on session end. Uses sendBeacon when
// available so it survives the tab-close race (page unload cancels
// in-flight fetches; navigator.sendBeacon is retained by the browser
// for delivery). Falls back to keepalive-fetch when Beacon can't
// carry credentials (rare) or on programmatic stop().
function _reportEnd(sessionId, durationSec, usage) {
  if (!sessionId) return;
  const url = `${API}/realtime/session/end`;
  const payload = JSON.stringify({
    session_id: sessionId,
    duration_sec: durationSec,
    usage,
  });
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch { /* fall through to fetch */ }
  try {
    fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}

async function _postSDP(path, sdp) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/sdp" },
    body: sdp,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = body?.detail || `HTTP ${res.status}`;
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = res.status;
    throw err;
  }
  return body;
}

// Pick the first MediaRecorder mime type this browser supports. Chrome/
// Edge prefer opus-in-webm, Safari understands mp4/aac. Falling back to
// "" lets the browser choose whatever it wants — the resulting blob
// still plays in an <audio> element with `type=""` because MediaRecorder
// tags the container itself.
function _pickRecorderMime() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const m of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch { /* older browsers throw here */ }
  }
  return "";
}

export class RealtimeSession {
  constructor({
    onEvent, onError, onOpen, onClose,
    onUserTurnAudio, onAssistantTurnAudio,
    onSessionAudio, onSessionAudioReady,
  } = {}) {
    this.pc = null;
    this.dataChannel = null;
    this.audioEl = null;
    this.micStream = null;
    this.remoteStream = null;
    this.onEvent = onEvent || (() => {});
    this.onError = onError || (() => {});
    this.onOpen = onOpen || (() => {});
    this.onClose = onClose || (() => {});
    // Per-turn audio callbacks. Fired with a Blob (webm/opus or mp4/aac
    // depending on browser) each time the user finishes a turn or the
    // model finishes speaking. VoiceMode attaches these to the history
    // row so the user can hit "Save" and download the audio.
    this.onUserTurnAudio      = onUserTurnAudio      || (() => {});
    this.onAssistantTurnAudio = onAssistantTurnAudio || (() => {});
    // Bulletproof "whole-session" callbacks. `onSessionAudioReady` is
    // called with a `getBlob()` function the moment the mix recorder
    // is running — used by the UI to enable the "Save session
    // recording" button. `onSessionAudio(blob)` is called with the
    // final Blob when the session ends.
    // Why a separate mix recorder? MediaRecorder directly on a WebRTC
    // remote stream is unreliable — many browsers produce empty or
    // silent audio. Routing both mic + remote through a Web Audio
    // graph into a MediaStreamAudioDestinationNode and recording
    // THAT works everywhere. This is the guaranteed path.
    this.onSessionAudio       = onSessionAudio       || (() => {});
    this.onSessionAudioReady  = onSessionAudioReady  || (() => {});
    this.closed = false;
    // Per-turn MediaRecorder state. We spin up a fresh recorder for
    // each turn instead of one long recording — that way each Blob
    // aligns 1:1 with a history row and playback is trivial (no need
    // to slice a big buffer by timestamp).
    this._userRecorder = null;
    this._userChunks = [];
    this._assistantRecorder = null;
    this._assistantChunks = [];
    // Whole-session mix recorder — Web Audio graph → mixed stream
    // → MediaRecorder. Always running while the session is live.
    this._sessionAudioCtx = null;
    this._sessionMixDest = null;
    this._sessionRecorder = null;
    this._sessionChunks = [];
    this._recorderMime = _pickRecorderMime();
    // Usage bookkeeping — aggregates every response.done usage block so
    // the admin cost dashboard is accurate. Also captures wall-clock
    // duration from the moment the data channel opens (which is when
    // billing actually starts, not the moment we clicked "start").
    this.hbmSessionId = null;
    this._startMs = null;
    this._usage = {
      audio_input_tokens: 0,
      audio_input_cached_tokens: 0,
      audio_output_tokens: 0,
      text_input_tokens: 0,
      text_input_cached_tokens: 0,
      text_output_tokens: 0,
    };
    this._reported = false;
  }

  _mergeUsage(usage) {
    if (!usage || typeof usage !== "object") return;
    // Realtime `usage` shape mirrors the Chat Completions shape but
    // with a `token_details` breakdown per modality. See:
    // developers.openai.com/api/docs/guides/realtime-costs
    const inDet  = usage.input_token_details  || {};
    const outDet = usage.output_token_details || {};
    const cachedDet = inDet.cached_tokens_details || {};

    const audioIn  = Number(inDet.audio_tokens)  || 0;
    const textIn   = Number(inDet.text_tokens)   || 0;
    const audioOut = Number(outDet.audio_tokens) || 0;
    const textOut  = Number(outDet.text_tokens)  || 0;

    // `cached_tokens` (total) can appear either at input_token_details
    // or nested; support both. When only the total is provided,
    // proportionally split between audio/text using their shares.
    const cachedAudio = Number(cachedDet.audio_tokens);
    const cachedText  = Number(cachedDet.text_tokens);
    const cachedTotal = Number(inDet.cached_tokens) || 0;

    let ac = Number.isFinite(cachedAudio) ? cachedAudio : NaN;
    let tc = Number.isFinite(cachedText)  ? cachedText  : NaN;
    if (!Number.isFinite(ac) || !Number.isFinite(tc)) {
      const denom = audioIn + textIn;
      if (denom > 0 && cachedTotal > 0) {
        ac = Math.round((cachedTotal * audioIn) / denom);
        tc = cachedTotal - ac;
      } else {
        ac = ac || 0;
        tc = tc || 0;
      }
    }

    this._usage.audio_input_tokens        += audioIn;
    this._usage.audio_input_cached_tokens += ac;
    this._usage.audio_output_tokens       += audioOut;
    this._usage.text_input_tokens         += textIn;
    this._usage.text_input_cached_tokens  += tc;
    this._usage.text_output_tokens        += textOut;
  }

  _reportUsageOnce() {
    if (this._reported) return;
    this._reported = true;
    const durationSec = this._startMs
      ? Math.max(0, (Date.now() - this._startMs) / 1000)
      : 0;
    _reportEnd(this.hbmSessionId, durationSec, this._usage);
  }

  // --- Per-turn audio recording ------------------------------------
  // We attach a fresh MediaRecorder to each stream at the START of a
  // turn and stop it at the END. The resulting Blob is handed back to
  // the caller (VoiceMode) which pins it on the history row.
  //
  // Why per-turn instead of one long recording?
  //   * The UI shows per-turn "Save" buttons — 1:1 alignment.
  //   * Small blobs are easier for users to download & share.
  //   * A crash mid-session doesn't lose earlier turns.
  //
  // Safety: recorders are best-effort. If MediaRecorder isn't supported
  // or throws mid-turn, we silently skip capture — the conversation
  // itself still works.

  _startUserRecorder() {
    if (!this.micStream || this._userRecorder) return;
    try {
      const opts = this._recorderMime ? { mimeType: this._recorderMime } : {};
      const rec = new MediaRecorder(this.micStream, opts);
      this._userChunks = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this._userChunks.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(this._userChunks, {
          type: rec.mimeType || "audio/webm",
        });
        this._userChunks = [];
        this._userRecorder = null;
        if (blob.size > 0) {
          try { this.onUserTurnAudio(blob); } catch { /* noop */ }
        }
      };
      rec.start(250);
      this._userRecorder = rec;
    } catch {
      /* no MediaRecorder support → skip capture, conversation continues */
    }
  }

  _stopUserRecorder() {
    const rec = this._userRecorder;
    if (!rec || rec.state === "inactive") return;
    try { rec.stop(); } catch { /* onstop still fires */ }
  }

  _startAssistantRecorder() {
    if (!this.remoteStream || this._assistantRecorder) return;
    try {
      const opts = this._recorderMime ? { mimeType: this._recorderMime } : {};
      const rec = new MediaRecorder(this.remoteStream, opts);
      this._assistantChunks = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this._assistantChunks.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(this._assistantChunks, {
          type: rec.mimeType || "audio/webm",
        });
        this._assistantChunks = [];
        this._assistantRecorder = null;
        if (blob.size > 0) {
          try { this.onAssistantTurnAudio(blob); } catch { /* noop */ }
        }
      };
      rec.start(250);
      this._assistantRecorder = rec;
    } catch {
      /* skip capture; audio still plays through <audio> */
    }
  }

  _stopAssistantRecorder() {
    const rec = this._assistantRecorder;
    if (!rec || rec.state === "inactive") return;
    try { rec.stop(); } catch { /* onstop still fires */ }
  }

  // --- Whole-session mix recorder (bulletproof path) --------------
  // Web Audio graph mixing mic + remote → MediaStreamAudioDestination
  // → MediaRecorder. Reliable across Chrome/Edge/Firefox/Safari 14+.
  // This is the audio the "Save session recording" button downloads.
  _startSessionMixRecorder() {
    if (this._sessionRecorder) return; // already running
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) {
        console.warn("[HBM realtime] AudioContext unavailable — session recording skipped.");
        return;
      }
      const ctx = new Ctx();
      // AudioContext often starts suspended when created outside a
      // direct user gesture. Resume immediately; ignore the promise
      // failure (Firefox <= 100 lacks resume).
      try { ctx.resume?.(); } catch { /* noop */ }
      const dest = ctx.createMediaStreamDestination();

      // Route local mic into the mix.
      if (this.micStream) {
        try { ctx.createMediaStreamSource(this.micStream).connect(dest); }
        catch (e) { console.warn("[HBM realtime] mic source failed:", e); }
      }
      // Route HBM (remote) into the mix AND to the speakers. Chrome's
      // WebRTC pipeline can only feed one consumer per stream — an
      // <audio> element OR a Web Audio source, not both. We used to
      // have an <audio> element, which meant createMediaStreamSource
      // silently got nothing → session recording had no HBM audio.
      // Now the graph handles both playback AND capture.
      if (this.remoteStream) {
        try {
          const remoteSrc = ctx.createMediaStreamSource(this.remoteStream);
          remoteSrc.connect(dest);              // → into recording mix
          remoteSrc.connect(ctx.destination);   // → out to speakers
        } catch (e) {
          console.warn("[HBM realtime] remote source failed:", e);
        }
      }

      const opts = this._recorderMime ? { mimeType: this._recorderMime } : {};
      const rec = new MediaRecorder(dest.stream, opts);
      this._sessionChunks = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this._sessionChunks.push(e.data);
      };
      rec.onstop = () => {
        const mime = rec.mimeType || "audio/webm";
        const blob = new Blob(this._sessionChunks, { type: mime });
        this._sessionRecorder = null;
        if (blob.size > 0) {
          try { this.onSessionAudio(blob); } catch { /* noop */ }
        }
      };
      // Emit chunks every 1s so a mid-session "save now" call can
      // grab whatever's been captured so far (see getSessionBlob).
      rec.start(1000);
      this._sessionAudioCtx = ctx;
      this._sessionMixDest = dest;
      this._sessionRecorder = rec;
      console.info("[HBM realtime] session mix recorder started · mime=%s", rec.mimeType || "?");
      try { this.onSessionAudioReady(); } catch { /* noop */ }
    } catch (e) {
      console.warn("[HBM realtime] session mix recorder failed:", e);
    }
  }

  _stopSessionMixRecorder() {
    const rec = this._sessionRecorder;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch { /* onstop still fires */ }
    }
    // Close the AudioContext to free the audio device — recorded blob
    // is already buffered in _sessionChunks / handed to onSessionAudio.
    try { this._sessionAudioCtx?.close?.(); } catch { /* noop */ }
    this._sessionAudioCtx = null;
    this._sessionMixDest = null;
  }

  /**
   * Return a Blob of everything recorded so far (mic + HBM mixed
   * together). Callable AT ANY TIME while the session is running —
   * the user can hit "Save session recording" mid-conversation and
   * get a snapshot without ending the call. Returns null if the mix
   * recorder never started (browser lacks Web Audio, etc.).
   *
   * IMPORTANT: MediaRecorder buffers pending audio internally between
   * the `start(timeslice)` intervals. If we just read `_sessionChunks`
   * we miss up-to-`timeslice`-worth of audio at the tail. On a short
   * conversation this can mean the ENTIRE blob is empty. Force a
   * `requestData()` call first so the buffered chunk is flushed
   * synchronously before we assemble the Blob.
   */
  async getSessionBlob() {
    const rec = this._sessionRecorder;
    if (rec && rec.state === "recording") {
      try {
        rec.requestData();
      } catch { /* not supported → we'll just miss the tail */ }
      // Give MediaRecorder a microtask tick to fire ondataavailable
      // with the flushed chunk before we read _sessionChunks.
      await new Promise((r) => setTimeout(r, 50));
    }
    if (!this._sessionChunks?.length) return null;
    const mime = rec?.mimeType || "audio/webm";
    return new Blob(this._sessionChunks, { type: mime });
  }

  hasSessionAudio() {
    return (this._sessionChunks?.length || 0) > 0;
  }

  async start() {
    // 1) Mint the ephemeral session — this also acts as our permission
    // gate (backend 403s if the user isn't `direct_voice_beta`). The
    // backend also returns `hbm_session_id` so we can post usage on
    // stop().
    const secret = await _postJSON("/realtime/session");
    this.hbmSessionId = secret?.hbm_session_id || null;

    // 2) Wire up the peer connection + local mic + remote audio playback.
    this.pc = new RTCPeerConnection();

    // Remote audio playback. We create the element for its onended /
    // lifecycle hooks, but MUTE it — actual playback goes through
    // the Web Audio graph (see _startSessionMixRecorder) so we can
    // BOTH play audio to speakers AND record it. If the element
    // wasn't muted, Chrome would take exclusive ownership of the
    // stream and the recording would be silent.
    this.audioEl = document.createElement("audio");
    this.audioEl.autoplay = true;
    this.audioEl.playsInline = true;
    this.audioEl.muted = true;
    this.audioEl.dataset.hbmRealtime = "1";
    document.body.appendChild(this.audioEl);
    this.pc.ontrack = (event) => {
      this.remoteStream = event.streams[0];
      this.audioEl.srcObject = this.remoteStream;
    };

    // Local mic. echoCancellation + noiseSuppression on so mic doesn't
    // pick up the model's own voice from the speakers.
    try {
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (e) {
      this.stop();
      const err = new Error(
        e?.name === "NotAllowedError"
          ? "Microphone permission denied."
          : "Couldn't access microphone.",
      );
      throw err;
    }
    this.micStream.getTracks().forEach((t) => this.pc.addTrack(t, this.micStream));

    // Events channel — model emits speech/text events here. We surface
    // 'response.audio_transcript.done' etc. so the UI can show a live
    // transcript beneath the orb.
    this.dataChannel = this.pc.createDataChannel(_EVENTS_CHANNEL);
    this.dataChannel.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const t = msg?.type || "";

        // Per-turn audio capture. OpenAI Realtime marks turn boundaries
        // with these events (server-VAD path). We use them to open/close
        // fresh MediaRecorders → each turn becomes its own downloadable
        // Blob. Docs: developers.openai.com/api/reference/realtime-server-events
        if (t === "input_audio_buffer.speech_started") {
          this._startUserRecorder();
        } else if (t === "input_audio_buffer.speech_stopped") {
          this._stopUserRecorder();
        } else if (
          t === "response.created" ||
          t === "output_audio_buffer.started" ||
          t === "response.output_audio.delta" ||
          t === "response.audio.delta"
        ) {
          // First audio-facing signal of an assistant turn — start if
          // not already running. Multiple events map to the same edge
          // depending on server-VAD vs client-VAD; the guard inside
          // _startAssistantRecorder handles duplicates.
          this._startAssistantRecorder();
        } else if (
          t === "output_audio_buffer.stopped" ||
          t === "response.audio.done" ||
          t === "response.output_audio.done" ||
          t === "response.done"
        ) {
          this._stopAssistantRecorder();
        }

        // Aggregate usage for the admin cost dashboard AND surface
        // cache-hit telemetry to the console for local debugging.
        // `cached_tokens` inside usage.input_token_details is populated
        // by OpenAI on every response.done — see docs:
        // developers.openai.com/api/docs/guides/realtime-costs
        if (t === "response.done") {
          const usage = msg?.response?.usage;
          if (usage) {
            this._mergeUsage(usage);
            const cached = usage?.input_token_details?.cached_tokens;
            if (typeof cached === "number" && cached > 0) {
              console.info(
                `[HBM realtime] cache hit: ${cached} tokens cached out of ${usage?.input_tokens || "?"} input`,
              );
            }
          }
        }
        this.onEvent(msg);
      } catch {
        /* non-JSON keepalive — ignore */
      }
    };
    this.dataChannel.onopen = () => {
      // Billing starts here — mark our wall-clock start so the admin
      // "minutes used" number reflects real conversation time, not
      // WebRTC handshake overhead.
      this._startMs = Date.now();
      // Start the reliable whole-session recorder. This mixes mic +
      // remote audio via Web Audio graph → MediaStreamDestination →
      // MediaRecorder. Guaranteed to capture BOTH sides even in
      // browsers where MediaRecorder-on-WebRTC-track fails silently.
      this._startSessionMixRecorder();
      // Cache-friendly session update: retention_ratio=0.8 makes
      // OpenAI drop history in bigger, less-frequent chunks instead
      // of the default "shift the window every turn" mode. That keeps
      // the system-prompt prefix stable for many more turns → higher
      // cache hit rate → 80× cheaper input tokens on Realtime.
      // Docs: developers.openai.com/api/docs/guides/realtime-costs
      try {
        this.dataChannel.send(JSON.stringify({
          type: "session.update",
          session: {
            truncation: {
              type: "retention_ratio",
              retention_ratio: 0.8,
            },
          },
        }));
      } catch { /* connection died in the same tick — onclose will fire */ }
      this.onOpen();
    };
    this.dataChannel.onclose = () => {
      this._reportUsageOnce();
      if (!this.closed) this.onClose();
    };
    this.pc.oniceconnectionstatechange = () => {
      if (this.pc && ["failed", "disconnected", "closed"].includes(this.pc.iceConnectionState)) {
        this._reportUsageOnce();
        if (!this.closed) this.onClose();
      }
    };

    // 3) SDP handshake through our backend proxy.
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    let answer;
    try {
      const { sdp } = await _postSDP("/realtime/negotiate", offer.sdp);
      answer = sdp;
    } catch (e) {
      this.stop();
      throw e;
    }
    await this.pc.setRemoteDescription({ type: "answer", sdp: answer });
  }

  // Push a model instruction / user text turn over the data channel.
  send(event) {
    if (this.dataChannel?.readyState !== "open") return false;
    this.dataChannel.send(JSON.stringify(event));
    return true;
  }

  stop() {
    this.closed = true;
    // Report BEFORE tearing down connections — tearing down first
    // sometimes cancels the beacon on Safari.
    this._reportUsageOnce();
    // Close any in-flight recorders so their final blob still fires
    // through the callback (MediaRecorder.stop() flushes remaining
    // chunks synchronously into onstop before the streams die below).
    this._stopUserRecorder();
    this._stopAssistantRecorder();
    this._stopSessionMixRecorder();
    try { this.dataChannel?.close(); } catch { /* noop */ }
    try {
      this.micStream?.getTracks().forEach((t) => t.stop());
    } catch { /* noop */ }
    try {
      if (this.audioEl) {
        this.audioEl.srcObject = null;
        this.audioEl.remove();
      }
    } catch { /* noop */ }
    try { this.pc?.close(); } catch { /* noop */ }
    this.pc = null;
    this.dataChannel = null;
    this.audioEl = null;
    this.micStream = null;
    this.remoteStream = null;
  }
}
